import React from "react";
import { Form, Row, Col, Spinner } from "react-bootstrap";
import TimezoneSelect from "react-timezone-select";
import defaultAvatar from "../../images/avatar.svg";
import { US_STATES } from "../../constants/global-constants";

const BasicInfo = ({
  formData,
  handleInputChange,
  handleImageUpload,
  handleImageClick,
  profile,
  validationErrors = {},
  isImageUploading,
  uploadedImageUrl,
}) => {
  // Handle both uploaded image URL and existing profile image
  const displayImageUrl =
    uploadedImageUrl || profile?.profile_image?.presigned_url || defaultAvatar;

  // Get the proper timezone object for display with GMT offset
  const getTimezoneValue = (timezoneCode) => {
    if (!timezoneCode) return "";

    // The react-timezone-select component expects just the timezone code as value
    // It will automatically format it with GMT offset for display
    return timezoneCode;
  };

  return (
    <>
      <h2 className="mb-3">Basic Information</h2>
      <Row className="g-3">
        <Col xs={12} className="text-center">
          <Form.Group className="mb-3 user-profile-image-container">
            <div className="profile-image-wrapper">
              <button
                type="button"
                onClick={handleImageClick("profile_image")}
                className="user-profile-button"
                disabled={isImageUploading}
              >
                <img
                  src={displayImageUrl}
                  alt="Profile"
                  className="user-profile-icon"
                  style={{ width: "120px", height: "120px" }}
                />
                {isImageUploading && (
                  <div className="upload-overlay">
                    <Spinner animation="border" size="sm" variant="light" />
                  </div>
                )}
              </button>
            </div>
            <Form.Control
              type="file"
              id="profile_image-input"
              className="d-none"
              accept="image/*"
              onChange={handleImageUpload("profile_image")}
              isInvalid={!!validationErrors.profile_image}
            />
            <Form.Label
              htmlFor="profile_image-input"
              className="btn btn-outline-primary"
              style={{ fontWeight: "500" }}
            >
              Upload Image
            </Form.Label>
            {validationErrors.profile_image && (
              <Form.Control.Feedback
                type="invalid"
                style={{ display: "block" }}
              >
                {validationErrors.profile_image}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group
            className={validationErrors.first_name ? "has-error" : ""}
          >
            <Form.Label className="required-field">First Name*</Form.Label>
            <Form.Control
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              required
              isInvalid={!!validationErrors.first_name}
              className={validationErrors.first_name ? "error-shake" : ""}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.first_name}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className={validationErrors.last_name ? "has-error" : ""}>
            <Form.Label className="required-field">Last Name*</Form.Label>
            <Form.Control
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              required
              isInvalid={!!validationErrors.last_name}
              className={validationErrors.last_name ? "error-shake" : ""}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.last_name}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col xs={12}>
          <Form.Group>
            <Form.Label>Address Line 1</Form.Label>
            <Form.Control
              type="text"
              name="user_address.address_line1"
              value={formData.user_address.address_line1}
              onChange={handleInputChange}
              isInvalid={!!validationErrors["user_address.address_line1"]}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors["user_address.address_line1"]}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col xs={12}>
          <Form.Group>
            <Form.Label>Address Line 2</Form.Label>
            <Form.Control
              type="text"
              name="user_address.address_line2"
              value={formData.user_address.address_line2}
              onChange={handleInputChange}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>City</Form.Label>
            <Form.Control
              type="text"
              name="user_address.city"
              value={formData.user_address.city}
              onChange={handleInputChange}
              isInvalid={!!validationErrors["user_address.city"]}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors["user_address.city"]}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>State</Form.Label>
            <Form.Select
              name="user_address.state"
              value={formData.user_address.state}
              onChange={handleInputChange}
              isInvalid={!!validationErrors["user_address.state"]}
            >
              <option value="">Select State</option>
              {US_STATES.map((state) => (
                <option key={state.value} value={state.value}>
                  {state.label}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {validationErrors["user_address.state"]}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Postal Code</Form.Label>
            <Form.Control
              type="text"
              name="user_address.zip_code"
              value={formData.user_address.zip_code}
              onChange={handleInputChange}
              isInvalid={!!validationErrors["user_address.zip_code"]}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors["user_address.zip_code"]}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className={validationErrors.phone ? "has-error" : ""}>
            <Form.Label className="required-field">Phone*</Form.Label>
            <Form.Control
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              required
              placeholder="(123) 456-7890"
              isInvalid={!!validationErrors.phone}
              className={validationErrors.phone ? "error-shake" : ""}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.phone}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Format: (123) 456-7890 or 123-456-7890
            </Form.Text>
          </Form.Group>
        </Col>
        <Col xs={12}>
          <Form.Group>
            <Form.Label>Time Zone</Form.Label>
            <TimezoneSelect
              value={getTimezoneValue(formData.timezone)}
              onChange={(timezone) =>
                handleInputChange({
                  target: { name: "timezone", value: timezone.value },
                })
              }
              placeholder="Select Time Zone"
            />
            {validationErrors.timezone && (
              <Form.Control.Feedback
                type="invalid"
                style={{ display: "block" }}
              >
                {validationErrors.timezone}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>
      </Row>
    </>
  );
};

export default BasicInfo;
